(function () {
  "use strict";

  const BUILD = "2026-07-22-jacquemus-grid-v8";
  const BADGE_ATTR = "data-nicha-collection-badge";
  const vendorCache = new Map();

  const GRID_CARD_SELECTOR =
    "body.nicha-collection-page .collection .card-wrapper, " +
    "body.nicha-collection-page .collection .product-card-wrapper, " +
    "body.nicha-product-page product-recommendations.product-recommendations .card-wrapper, " +
    "body.nicha-product-page .product-recommendations .card-wrapper";

  const isCollectionPage = () => {
    const path = (window.location.pathname || "").toLowerCase();
    return /\/collections\/[^/]+/.test(path) && !/\/products\//.test(path);
  };

  const isProductPage = () => {
    const path = (window.location.pathname || "").toLowerCase();
    return /\/products\/[^/]+/.test(path);
  };

  const isEnglishLocale = () => {
    const locale = (window.Shopify?.locale || document.documentElement.lang || "fr").toLowerCase();
    return locale === "en" || locale.startsWith("en-");
  };

  const getCustomizableLabel = () => (isEnglishLocale() ? "CUSTOMIZABLE" : "PERSONNALISABLE");
  const getReadyToGoLabel = () => (isEnglishLocale() ? "READY TO GO" : "PRÊT À PARTIR");

  const normalizeText = (text) =>
    (text || "")
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

  const isReadyToGoHandle = (handle) => {
    const h = normalizeText(handle);
    return h.includes("ready-to-go") || h.includes("pret-a-partir");
  };

  const isReadyToGoVendor = (vendor) => {
    const v = normalizeText(vendor);
    return (v.includes("pret") && v.includes("partir")) || (v.includes("ready") && v.includes("go"));
  };

  const isReadyToGoCollection = (handle) => {
    const h = normalizeText(handle);
    if (h.includes("pret-a-partir-homme") || h.includes("ready-to-go-menswear")) return false;
    if (h.includes("pret-a-partir")) return true;
    return h.includes("ready-to-go");
  };

  const getCollectionHandle = () => {
    const match = (window.location.pathname || "").match(/\/collections\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : "";
  };

  const getProductHandleFromCard = (card) => {
    const link =
      card.querySelector('.card__heading a[href*="/products/"]') ||
      card.querySelector('a[href*="/products/"]');
    if (!link) return "";
    const match = (link.getAttribute("href") || "").match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : "";
  };

  const getShopifyRoot = () => {
    const root = window.Shopify?.routes?.root;
    return root ? (root.endsWith("/") ? root : `${root}/`) : "/";
  };

  const fetchProductVendor = async (handle) => {
    if (!handle) return "";
    if (vendorCache.has(handle)) return vendorCache.get(handle);
    try {
      const response = await fetch(`${getShopifyRoot()}products/${encodeURIComponent(handle)}.js`);
      if (!response.ok) return "";
      const vendor = (await response.json()).vendor || "";
      vendorCache.set(handle, vendor);
      return vendor;
    } catch {
      return "";
    }
  };

  const fetchVendorMap = async (collectionHandle) => {
    if (!collectionHandle) return new Map();
    const map = new Map();
    let page = 1;
    let hasMore = true;
    while (hasMore && page <= 4) {
      const url = `${getShopifyRoot()}collections/${encodeURIComponent(collectionHandle)}/products.json?limit=250&page=${page}`;
      try {
        const response = await fetch(url);
        if (!response.ok) break;
        const products = (await response.json()).products || [];
        products.forEach((product) => {
          map.set(product.handle, product.vendor || "");
          vendorCache.set(product.handle, product.vendor || "");
        });
        hasMore = products.length === 250;
        page += 1;
      } catch {
        break;
      }
    }
    return map;
  };

  const readVendorFromCard = (card) =>
    (card.querySelector(".card-information .caption-with-letter-spacing")?.textContent || "").trim();

  const getBadgeLabel = (handle, vendorMap, collectionHandle, card, vendorOverride) => {
    if (isReadyToGoCollection(collectionHandle) || isReadyToGoHandle(handle)) {
      return getReadyToGoLabel();
    }
    const vendor =
      vendorOverride ||
      vendorMap.get(handle) ||
      vendorCache.get(handle) ||
      readVendorFromCard(card) ||
      "";
    return isReadyToGoVendor(vendor) ? getReadyToGoLabel() : getCustomizableLabel();
  };

  const ensureBadge = (card, label) => {
    if (!card) return;
    const anchor = card.querySelector(".card__inner.ratio, .card__inner, .card__media");
    if (!anchor) return;

    let badge = anchor.querySelector(":scope > .nicha-collection-badge");
    if (!badge) {
      badge = document.createElement("span");
      badge.className = "nicha-collection-badge";
      anchor.insertBefore(badge, anchor.firstChild);
    }

    badge.textContent = label;
    card.setAttribute(BADGE_ATTR, "1");
  };

  const applyBadges = async (vendorMap, collectionHandle) => {
    const wrappers = document.querySelectorAll(GRID_CARD_SELECTOR);
    if (!wrappers.length) return;

    await Promise.all(
      Array.from(wrappers).map(async (wrapper) => {
        const card = wrapper.querySelector(".card") || wrapper;
        const handle = getProductHandleFromCard(card);
        if (!handle) return;

        let vendor = vendorMap.get(handle) || vendorCache.get(handle) || readVendorFromCard(card);
        if (!vendor && isProductPage()) {
          vendor = await fetchProductVendor(handle);
        }

        ensureBadge(card, getBadgeLabel(handle, vendorMap, collectionHandle, card, vendor));
      })
    );
  };

  const observeGrid = (state, roots) => {
    roots.forEach((root) => {
      if (!root || root.dataset.nichaGridObserver === "true") return;
      root.dataset.nichaGridObserver = "true";

      let scheduled = null;
      new MutationObserver(() => {
        if (scheduled) return;
        scheduled = setTimeout(() => {
          scheduled = null;
          applyBadges(state.vendorMap, state.collectionHandle).catch(() => {});
        }, 200);
      }).observe(root, { childList: true, subtree: true });
    });
  };

  const initCollection = async () => {
    if (!isCollectionPage()) return;

    document.body.classList.add("nicha-collection-page");

    const collectionHandle = getCollectionHandle();
    const state = { vendorMap: new Map(), collectionHandle };
    const collectionRoot = document.querySelector(".collection");

    await applyBadges(state.vendorMap, collectionHandle);
    observeGrid(state, [collectionRoot].filter(Boolean));

    fetchVendorMap(collectionHandle)
      .then((vendorMap) => {
        state.vendorMap = vendorMap;
        return applyBadges(vendorMap, collectionHandle);
      })
      .catch(() => {});
  };

  const initProductRecommendations = async () => {
    if (!isProductPage()) return;

    document.body.classList.add("nicha-product-page");

    const state = { vendorMap: new Map(), collectionHandle: "" };
    await applyBadges(state.vendorMap, state.collectionHandle);

    const roots = Array.from(
      document.querySelectorAll("product-recommendations.product-recommendations, .product-recommendations")
    );
    observeGrid(state, roots);
  };

  const init = async () => {
    try {
      if (isCollectionPage()) await initCollection();
      if (isProductPage()) await initProductRecommendations();
    } catch {
      /* noop */
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      init().catch(() => {});
    });
  } else {
    init().catch(() => {});
  }

  document.addEventListener("shopify:section:load", () => {
    init().catch(() => {});
  });

  window.NichaCollectionGrid = { build: BUILD, refresh: init };
})();
